/**
 * The HTTP client every request goes through.
 *
 * Two behaviours make this worth centralising:
 *
 *   1. **Transparent refresh, de-duplicated.** When a page mounts, six queries
 *      can fire at once and all six can get a 401. Refreshing per request would
 *      rotate the refresh token six times, and our own reuse detection (ADR
 *      0004) would read that as a stolen token and revoke the whole family —
 *      logging the user out for loading a page. So the first 401 starts a
 *      refresh and the rest await the same promise.
 *   2. **problem+json parsing.** Every failure arrives as an {@link ApiError}
 *      carrying a stable `type`, so callers branch on the contract rather than
 *      on message text.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 5.10, ADR 0004
 */
import { ApiError, networkProblem, parseProblem } from './problem';

/** Where the API lives. Same-origin in development via the Vite proxy. */
export const API_BASE = '/api/v1';

/** Paths that must never trigger a refresh attempt. */
const AUTH_PATHS = new Set(['/auth/login', '/auth/refresh', '/auth/logout']);

/** The in-flight access token. Memory only — never localStorage. */
let accessToken: string | null = null;

/**
 * The refresh currently in flight, if any.
 *
 * This single variable is what turns a burst of 401s into one refresh.
 */
let refreshInFlight: Promise<boolean> | null = null;

/** Called when refresh fails and the session is over. */
let onSessionExpired: (() => void) | null = null;

/** Stores the access token in memory. */
export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Registers the callback invoked when the session cannot be renewed. */
export function setSessionExpiredHandler(handler: (() => void) | null): void {
  onSessionExpired = handler;
}

/**
 * Invokes the session-expired handler, if one is registered.
 *
 * Exported so a caller can signal expiry without owning the handler — and so
 * a test can exercise the path without a backdoor into module internals.
 */
export function notifySessionExpired(): void {
  onSessionExpired?.();
}

/**
 * Resets module state.
 *
 * Exported for tests: this module holds process-wide mutable state, and a
 * token left behind by one test would silently authenticate the next.
 */
export function resetApiClient(): void {
  accessToken = null;
  refreshInFlight = null;
  onSessionExpired = null;
}

/** Options accepted by {@link apiFetch}, minus the ones it sets itself. */
export interface RequestOptions extends Omit<RequestInit, 'body'> {
  /** Serialised as JSON unless it is already a `BodyInit`. */
  body?: unknown;
  /** Appended as a query string, skipping null and undefined. */
  query?: Record<string, string | number | boolean | null | undefined>;
}

/** Builds a URL with its query string. */
export function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${API_BASE}${path}`;
  if (!query) return url;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    /* An absent filter must not become `?stage=undefined`, which the API would
       reject as a validation error rather than ignore. */
    if (value === null || value === undefined || value === '') continue;
    params.append(key, String(value));
  }

  const search = params.toString();
  return search === '' ? url : `${url}?${search}`;
}

/**
 * Exchanges the refresh cookie for a new access token.
 *
 * Concurrent callers share one attempt — see the note at the top of the file.
 */
async function refreshAccessToken(): Promise<boolean> {
  refreshInFlight ??= (async (): Promise<boolean> => {
    try {
      const response = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        /* The refresh token is an httpOnly cookie; it is never read by JS. */
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) {
        accessToken = null;
        notifySessionExpired();
        return false;
      }

      const body = (await response.json()) as { accessToken?: string };
      if (typeof body.accessToken !== 'string') {
        accessToken = null;
        notifySessionExpired();
        return false;
      }

      accessToken = body.accessToken;
      return true;
    } catch {
      accessToken = null;
      notifySessionExpired();
      return false;
    } finally {
      /* Cleared regardless of outcome, so a later 401 can try again rather
         than awaiting a settled promise forever. */
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/** Performs one request, without the retry logic. */
async function performRequest(path: string, options: RequestOptions): Promise<Response> {
  const { body, query, headers, ...rest } = options;

  const isBodyInit =
    body instanceof FormData || body instanceof Blob || typeof body === 'string';

  const requestHeaders = new Headers(headers);
  requestHeaders.set('Accept', 'application/json');

  if (body !== undefined && !isBodyInit) {
    requestHeaders.set('Content-Type', 'application/json');
  }

  if (accessToken !== null) {
    requestHeaders.set('Authorization', `Bearer ${accessToken}`);
  }

  return fetch(buildUrl(path, query), {
    ...rest,
    headers: requestHeaders,
    credentials: 'include',
    ...(body === undefined
      ? {}
      : { body: isBodyInit ? (body as BodyInit) : JSON.stringify(body) }),
  });
}

/**
 * Calls the API.
 *
 * @throws {ApiError} on any non-2xx response, and on a network failure.
 */
export async function apiFetch<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  let response: Response;

  try {
    response = await performRequest(path, options);
  } catch (error) {
    throw new ApiError(
      networkProblem(error instanceof Error ? error.message : 'Request failed'),
    );
  }

  /* A 401 on an auth endpoint is the answer, not a prompt to refresh: retrying
     /auth/login after bad credentials would loop. */
  if (response.status === 401 && !AUTH_PATHS.has(path)) {
    const refreshed = await refreshAccessToken();

    if (refreshed) {
      try {
        response = await performRequest(path, options);
      } catch (error) {
        throw new ApiError(
          networkProblem(error instanceof Error ? error.message : 'Request failed'),
        );
      }
    }
  }

  if (!response.ok) {
    throw new ApiError(await parseProblem(response));
  }

  /* 204 and any empty body: `response.json()` would throw on an empty string. */
  if (response.status === 204 || response.headers.get('Content-Length') === '0') {
    return undefined as T;
  }

  return (await response.json()) as T;
}

/**
 * Fetches a file (non-JSON) through the same auth and refresh path as
 * {@link apiFetch}, returning the raw {@link Blob}.
 *
 * A CSV export cannot go through `apiFetch` (which parses JSON) nor through a
 * plain anchor `href` (which carries no `Authorization` header — the access
 * token lives in memory, never a cookie). This keeps the 401-refresh behaviour
 * so an export does not fail the moment an access token has aged out.
 *
 * @throws {ApiError} on any non-2xx response, and on a network failure.
 */
export async function apiDownload(
  path: string,
  options: RequestOptions = {},
): Promise<Blob> {
  let response: Response;

  try {
    response = await performRequest(path, options);
  } catch (error) {
    throw new ApiError(
      networkProblem(error instanceof Error ? error.message : 'Request failed'),
    );
  }

  if (response.status === 401 && !AUTH_PATHS.has(path)) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      try {
        response = await performRequest(path, options);
      } catch (error) {
        throw new ApiError(
          networkProblem(error instanceof Error ? error.message : 'Request failed'),
        );
      }
    }
  }

  if (!response.ok) {
    throw new ApiError(await parseProblem(response));
  }

  return response.blob();
}

/** Convenience wrappers. */
export const api = {
  get: <T>(path: string, options?: RequestOptions): Promise<T> =>
    apiFetch<T>(path, { ...options, method: 'GET' }),

  post: <T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> =>
    apiFetch<T>(path, { ...options, method: 'POST', body }),

  patch: <T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> =>
    apiFetch<T>(path, { ...options, method: 'PATCH', body }),

  put: <T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> =>
    apiFetch<T>(path, { ...options, method: 'PUT', body }),

  delete: <T>(path: string, options?: RequestOptions): Promise<T> =>
    apiFetch<T>(path, { ...options, method: 'DELETE' }),
};
